import React, { useState, useEffect } from "react";
import { Button, Typography, Box, LinearProgress, Alert, Paper, TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";

export default function UploadStudents() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [studentSummary, setStudentSummary] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage("Please select a file first");
      return;
    }
    setUploading(true);
    setMessage("");
    const user = JSON.parse(sessionStorage.getItem("user"));
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/students/upload-students/${user.client_id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setMessage(res.data.message);
      fetchStudentSummary(); // refresh table after upload
    } catch (err) {
      if (err.response) {
        setMessage(err.response.data?.message || "Upload failed");
      } else if (err.request) {
        setMessage("No response from server. Please try again later.");
      } else {
        setMessage(`Upload failed: ${err.message}`);
      }
    }
    setUploading(false);
  };

  const handleDownloadTemplate = () => {
    const csvContent =
      "student_id,college_id,admittedbatch_id,branch_id,user_id,admission_no,student_rollno,aadhaar_no,student_name,dob,gender,email,mobile\n" +
      "1,101,2023,501,1,AD123,RL123,123456789012,John Doe,2000-01-01,M,john@example.com,9876543210";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "students_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ✅ Fetch summary from backend
  const fetchStudentSummary = async () => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    setLoadingSummary(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/students/student-summary/${user.client_id}/${user.college_id}`
      );
      setStudentSummary(res.data.data || []);
      setTotalCount(res.data.data.length);
    } catch (err) {
      console.error(err);
      setMessage("Failed to fetch student summary");
    }
    setLoadingSummary(false);
  };
  const filteredRows = studentSummary
    .map((row, index) => ({ id: index + 1, sno: index + 1, ...row }))
    .filter((row) => {
      return Object.values(row).some((value) =>
        value?.toString().toLowerCase().includes(searchText.toLowerCase())
      );
    });
  useEffect(() => {
    fetchStudentSummary();
  }, []);

  // ✅ Columns for DataGrid
  const columns = [
    { field: "sno", headerName: "S.No", width: 70 },
    { field: "program_name", headerName: "Program", flex: 1 },
    { field: "admitted_batch", headerName: "AdmittedBatch", flex: 1 },
    { field: "branch_name", headerName: "Branch", flex: 1 },
    { field: "count", headerName: "Students Count", flex: 1, type: "number", align: "center" },
  ];

  return (
    <Box sx={{ p: 3, mx: "auto" }}>
      <Typography variant="h5" gutterBottom>
        Upload Students CSV
      </Typography>

      {/* File Upload Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          alignItems: "center",
          mb: 2,
          flexWrap: "nowrap",
        }}
      >
        <Button
          variant="contained"
          color="success"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadTemplate}
        >
          Download CSV
        </Button>

        <input
          accept=".csv,.xlsx"
          style={{ display: "none" }}
          id="contained-button-file"
          type="file"
          onChange={handleFileChange}
        />
        <label htmlFor="contained-button-file">
          <Button
            variant="contained"
            component="span"
            startIcon={<CloudUploadIcon />}
            sx={{
              backgroundColor: (theme) => theme.palette.success.main,
              color: "#fff",
              "&:hover": {
                backgroundColor: (theme) => theme.palette.success.dark,
              },
            }}
          >
            Choose File
          </Button>
        </label>
        <Button variant="contained" color="success" onClick={handleUpload} disabled={uploading}>
          Upload CSV
        </Button>
      </Box>

      {file && (
        <Typography variant="body1" sx={{ mb: 2 }}>
          Selected: {file.name}
        </Typography>
      )}

      {uploading && <LinearProgress sx={{ mt: 2 }} />}

      {message && (
        <Alert severity={message.includes("success") ? "success" : "error"} sx={{ mt: 2 }}>
          {message}
        </Alert>
      )}

      {/* ✅ Student Summary Section */}
      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
        Admitted batch wise Students by Program / Batch / Branch
      </Typography>
      <Box sx={{ width: { xs: "100%", sm: 300, md: 400 }, mb: 2 }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          fullWidth
        />
      </Box>
      <Paper
        elevation={3}
        sx={{
          height: { xs: 400, md: 500 }, // adaptive height for mobile & desktop
          p: 2,
          width: "100%", // make width flexible
          overflowX: "auto", // enable horizontal scroll on small screens
        }}
      >
        <Box sx={{ minWidth: 600, height: 500 }}>
          {" "}
          {/* ensures DataGrid content doesn't shrink too small */}
          <DataGrid
            rows={filteredRows}
            columns={columns}
            loading={loadingSummary}
            pagination
            paginationMode="server"
            rowCount={totalCount} // total records from backend
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => setPageSize(newSize)}
            pageSizeOptions={[2, 5, 10, 20, 50]}
            autoHeight={false} // keep consistent height
            sx={{
              width: "100%",
              "& .MuiDataGrid-virtualScroller": {
                overflowX: "auto", // ensures DataGrid itself scrolls horizontally if needed
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
